import { computed, type Ref } from "vue";
import startCase from "lodash/startCase";
import intersection from "lodash/intersection";
import capitalize from "lodash/capitalize";

import TicketConfirmationModal from "@/components/modals/TicketConfirmationModal.vue";
import AllowBlockEmailTicketModal from "@/components/modals/AllowBlockEmailTicketModal.vue";
import AllowOutboundProxyEmailModal from "@/components/modals/AllowOutboundProxyEmailModal.vue";
import ApplyQuickFixModal from "@/components/modals/ApplyQuickFixModal.vue";
import BulkAddDataGovernancePermissionsModal from "@/components/modals/BulkAddDataGovernancePermissionsModal.vue";
import ContactUserModal from "@/components/modals/ContactUserModal.vue";

import { useDialogsStore, type DialogDataConfig } from "@/_store/dialogs.module";
import type { TicketActionWithSeverity, TicketDetails } from "@/_store/tickets/tickets.module";

import {
  TicketAction,
  TicketActionSeverity,
  TicketEventSubtype,
  TicketTrigger,
  ViolationDirection,
  quickFixStepsMap,
  userDataGovernanceUnifiedEventTypes,
} from "@/constants/tickets";
import { GtmAction, GTMParams } from "@/constants/gtm";
import { ModalWidth } from "@/constants/modals";
import { ProxyDirection } from "@/constants/email-proxy";
import { Service } from "@/constants/cloud-apps";

import { trackGtmEvent } from "@/_helpers/analytics";
import { isGlobalActionRestricted } from "@/_helpers/global-permissions";
import {
  approveEmailActions,
  discardEmailActions,
  getTicketActionsList,
  sortTicketActions,
  type QuickFixPayload,
} from "@/_helpers/ticket-helpers";

import type { GenericCallback } from "@/types";
import { i18n } from "@/plugins/i18n.ts";
import {
  componentDialogsConfigConstructor,
  confirmationDialogsConfigConstructor,
} from "@/_helpers/dialog-helpers.ts";
import { GlobalRoleScopes, GlobalRoleScopeSection } from "@/constants/roles.ts";

export interface ContactUserCallbackItem {
  includeEventDetails: boolean;
  contactMessage: string;
  recipients: string[];
  id: string;
  workspaceName?: string;
}

export type TicketActionPayload<TTicketDetails extends TicketDetails> = DialogDataConfig<
  TTicketDetails & { name?: string; closeTicket?: boolean },
  TicketAction
>;

export type TicketQuickFixActionPayload<TTicketDetails extends TicketDetails> = DialogDataConfig<
  QuickFixPayload<TTicketDetails>,
  TicketAction
>;

/**
 * Adapter that exposes store actions and environment-specific behavior
 * while keeping the composable UI-agnostic.
 */
export interface TicketPreviewHeaderAdapter<TTicketDetails extends TicketDetails> {
  applyAction: (payload: TicketActionPayload<TTicketDetails>) => Promise<void>;
  applyDownloadAction: (payload: TicketActionPayload<TTicketDetails>) => Promise<void>;
  applyQuickFixAction: (payload: TicketQuickFixActionPayload<TTicketDetails>) => Promise<void>;
  getRecipients: (eventId: string, isDevice: boolean, workspaceId?: string) => Promise<string[]>;
  isActionRestricted: (ticket: TTicketDetails, severity: TicketActionSeverity) => boolean;
  onContactUser?: (payload: DialogDataConfig<ContactUserCallbackItem>) => Promise<void>;
  checkProxyConfig?: (
    action: TicketAction,
    severity: TicketActionSeverity,
    ticket: TTicketDetails
  ) => boolean;
  configureProxy?: (direction: ProxyDirection) => void;
  shouldCloseTicket?: () => boolean;
  gtmContext: GTMParams.MSP | GTMParams.SINGLE_VIEW;
  getDisclaimer?: (disable: boolean) => string | undefined;
  onTestMxRecord?: (item: TTicketDetails, severity: TicketActionSeverity) => void;
}

const standardConfirmationActions = [
  TicketAction.SUSPEND_FROM_SERVICE,
  TicketAction.ENFORCE_UAC,
  TicketAction.ALLOW_NO_ENCRYPTION,
  TicketAction.ENCRYPT_DRIVE,
  TicketAction.ENABLE_FIREWALL,
  TicketAction.BLOCK_MALICIOUS_URL_CLICK,
  TicketAction.ALLOW_MALICIOUS_URL_CLICK,
];

/**
 * Canonical ticket metadata used by confirmation modals.
 */
interface TicketMetadata {
  serviceName: string;
  violationUsers: string;
  violationDirection: ViolationDirection;
  violationTrigger: string;
}

/**
 * Normalize ticket fields for modal templates and interpolation.
 */
const getTicketMetadata = <TTicketDetails extends TicketDetails>(
  item: TTicketDetails
): TicketMetadata => {
  const serviceNameRaw = item.serviceName ?? item.sections.serviceName;
  const serviceName = i18n.global.t(`services.${serviceNameRaw}`);
  const violationUsers = (item?.sections?.userDataAccessViolation?.users ?? []).join(",");
  const violationDirection =
    item?.sections?.userDataAccessViolation?.violationDirection ?? ViolationDirection.SHARE;
  const violationTrigger = startCase(
    i18n.global.t(`ticketDetails.fullDetails.accessViolations.${item.eventTrigger}`)
  );

  return {
    serviceName,
    violationUsers,
    violationDirection,
    violationTrigger,
  };
};

/**
 * Resolve confirmation description text with optional override.
 */
const getConfirmationText = <TTicketDetails extends TicketDetails>(
  action: TicketAction,
  item: TTicketDetails,
  metadata: TicketMetadata,
  text = ""
): string => {
  if (text) {
    return text;
  }

  return i18n.global.t(`modals.${action}.description`, {
    ...(item as Record<string, unknown>),
    serviceName: metadata.serviceName,
    violationUsers: metadata.violationUsers,
    violationDirection: metadata.violationDirection,
    violationTrigger: metadata.violationTrigger,
  });
};

/**
 * Compute shared flags and disclaimer used across action handlers.
 */
const getCommonProps = <TTicketDetails extends TicketDetails>(
  item: TTicketDetails,
  adapter: TicketPreviewHeaderAdapter<TTicketDetails>,
  disable: boolean
) => {
  const showCheckbox =
    !item.processed && !adapter.isActionRestricted(item, TicketActionSeverity.LOW);
  const includeCloseTicket = typeof adapter.shouldCloseTicket === "function";
  const closeTicket = includeCloseTicket ? adapter.shouldCloseTicket!() : false;
  const disclaimer = adapter.getDisclaimer
    ? adapter.getDisclaimer(disable)
    : disable
      ? i18n.global.t("general.noPermissions")
      : undefined;

  return {
    closeTicket,
    disclaimer,
    includeCloseTicket,
    showCheckbox,
  };
};

interface ProxyGuardParams<TTicketDetails extends TicketDetails> {
  action: TicketAction;
  severity: TicketActionSeverity;
  item: TTicketDetails;
  adapter: TicketPreviewHeaderAdapter<TTicketDetails>;
  dialogsStore: ReturnType<typeof useDialogsStore>;
}

/**
 * Early-exit guard that opens the proxy configuration dialog when required.
 * Returns true when a redirect is triggered to prevent double-handling.
 */
const handleProxyGuard = async <TTicketDetails extends TicketDetails>({
  action,
  severity,
  item,
  adapter,
  dialogsStore,
}: ProxyGuardParams<TTicketDetails>): Promise<boolean> => {
  if (!adapter.checkProxyConfig) {
    return false;
  }

  const shouldConfigure = adapter.checkProxyConfig(action, severity, item);
  const proxyDirection = item.sections?.emailMetaData?.emailProxyDirection;

  if (shouldConfigure && adapter.configureProxy && proxyDirection) {
    dialogsStore.openDialog({
      ...confirmationDialogsConfigConstructor({
        action: TicketAction.CONFIGURE_PROXY,
        item: {
          proxyDirection: capitalize(proxyDirection),
        },
        callback: () => adapter.configureProxy?.(proxyDirection),
      }),
      header: {
        title: i18n.global.t("modals.configureProxy.title", {
          proxyDirection: i18n.global.t(`emailProxy.directions.${proxyDirection}`),
        }),
        titleClass: "text-red-dark",
        titleIcon: "warning",
      },
    });
    return true;
  }

  return false;
};

/**
 * Shared confirmation config builder for actions that can use either a standard
 * confirmation dialog or a checkbox-based confirmation modal.
 */
interface ConfirmationConfigOptions<TTicketDetails extends TicketDetails> {
  action: TicketAction;
  item: TTicketDetails;
  disable: boolean;
  callback?: GenericCallback;
  showCheckbox?: boolean;
  closeTicket?: boolean;
  checkboxText?: string;
  text?: string;
  disclaimer?: string;
  warning?: string;
}

const blocklistedSenderDescriptionSubTypes: TicketEventSubtype[] = [
  TicketEventSubtype.TLD,
  TicketEventSubtype.COUNTRY_BLOCKLIST,
  TicketEventSubtype.WILDCARD_DOMAIN_BLOCKLIST,
  TicketEventSubtype.IP_BLOCKLIST,
];

const getBlocklistedSenderDescription = (ticket: TicketDetails): string => {
  const subTypes = ticket.sections?.subTypes ?? [];
  const subType = blocklistedSenderDescriptionSubTypes.find((value) => subTypes.includes(value));
  const sender = ticket.sections?.emailMetaData?.senderEmail ?? "";

  if (!subType || (!sender && subType !== TicketEventSubtype.IP_BLOCKLIST)) {
    return "";
  }

  return i18n.global.t(
    `modals.${TicketAction.GENERAL_APPROVE}.blocklistedSenderSubTypes.${subType}`,
    { sender }
  );
};

/**
 * Centralizes header presentation state and action handling for ticket headers.
 * Accepts the current ticket as a ref so computed values stay reactive.
 */
export function useTicketPreviewHeader<TTicketDetails extends TicketDetails>(
  ticket: Ref<TTicketDetails>,
  adapter: TicketPreviewHeaderAdapter<TTicketDetails>
) {
  const dialogsStore = useDialogsStore();

  const actionsList = computed(() => {
    // Derive and order actions once for menus and redirect handling.
    const actions = getTicketActionsList(ticket.value.actions, ticket.value.sections?.serviceName);
    return sortTicketActions(actions);
  });

  /**
   * Builds a confirmation config with optional checkbox for "close ticket"
   * and injects derived fields for violation messaging.
   */
  const createTicketModalWithConfirmationConfig = (
    options: ConfirmationConfigOptions<TTicketDetails>
  ) => {
    const {
      action,
      item,
      disable,
      callback = adapter.applyAction,
      showCheckbox = false,
      closeTicket = false,
      checkboxText = i18n.global.t("general.closeUponConfirmation"),
      text = "",
      disclaimer,
      warning = "",
    } = options;
    const metadata = getTicketMetadata(item);
    const modalText = getConfirmationText(action, item, metadata, text);
    const resolvedDisclaimer = Object.prototype.hasOwnProperty.call(options, "disclaimer")
      ? disclaimer
      : getCommonProps(item, adapter, disable).disclaimer;
    const itemWithMetadata = {
      ...item,
      ...metadata,
    };

    // The checkbox and the warning banner both live inside TicketConfirmationModal,
    // so either one moves the dialog off the plain html confirmation.
    if (showCheckbox || warning) {
      const dialogConfig = componentDialogsConfigConstructor({
        item: itemWithMetadata,
        action,
        callback,
        component: TicketConfirmationModal,
        disable,
        disclaimer: resolvedDisclaimer,
        width: ModalWidth.SMALL,
      });
      dialogConfig.data.item.showCheckbox = showCheckbox;
      dialogConfig.data.item.checkboxText = checkboxText;
      dialogConfig.data.item.closeTicket = closeTicket;
      dialogConfig.data.item.text = modalText;
      dialogConfig.data.item.warning = warning;
      return dialogConfig;
    }

    return confirmationDialogsConfigConstructor({
      item: itemWithMetadata,
      action,
      disclaimer: resolvedDisclaimer,
      callback,
      disable,
      text: modalText,
    });
  };

  /**
   * Opens the Contact User modal, resolving recipients based on device/user event.
   */
  const handleContactUserAction = async (
    item: TTicketDetails,
    disable: boolean,
    disclaimer: string | undefined
  ) => {
    if (!adapter.onContactUser) {
      return;
    }

    const isDeviceEvent = !!item.lastLoggedInUsers?.length;
    const recipients = await adapter.getRecipients(item.eventId, isDeviceEvent, item.workspaceId);

    dialogsStore.openDialog(
      componentDialogsConfigConstructor({
        action: TicketAction.CONTACT_USER,
        item: {
          id: item.eventId,
          isDeviceEvent,
          workspaceId: item.workspaceId,
          recipients,
        },
        callback: adapter.onContactUser,
        component: ContactUserModal,
        disable,
        disclaimer,
        width: ModalWidth.LARGE,
      })
    );
  };

  /**
   * Opens the General Approve modal with special descriptions for proxy and
   * specific authentication-related triggers.
   */
  const handleGeneralApproveAction = async (
    item: TTicketDetails,
    showCheckbox: boolean,
    disable: boolean,
    disclaimer: string | undefined
  ) => {
    const action = TicketAction.GENERAL_APPROVE;
    const isProxy =
      item.sections?.serviceName === Service.PROXY ? i18n.global.t(`modals.${action}.proxy`) : "";

    const applySpecificDescription = [
      TicketTrigger.MISSING_REQUIRED_AUTHENTICATION,
      TicketTrigger.CROWDBLOCKED_SENDER,
      TicketTrigger.BLOCKLISTED_SENDER,
    ].includes(item.eventTrigger);

    const getSpecificDescription = () => {
      const subTypeDescription =
        item.eventTrigger === TicketTrigger.BLOCKLISTED_SENDER
          ? getBlocklistedSenderDescription(item)
          : "";

      return (
        subTypeDescription ||
        i18n.global.t(`modals.${action}.specificDescription.${item.eventTrigger}`)
      );
    };

    const text = applySpecificDescription
      ? [getSpecificDescription(), isProxy].filter(Boolean).join(" ")
      : i18n.global.t(`modals.${action}.description`);

    const dialogConfig = createTicketModalWithConfirmationConfig({
      action,
      item,
      disable,
      callback: adapter.applyAction,
      showCheckbox,
      closeTicket: false,
      checkboxText: i18n.global.t("general.closeUponConfirmation"),
      text,
      disclaimer,
    });

    dialogConfig.data.item.text = text;

    dialogsStore.openDialog(dialogConfig);
  };

  /**
   * Opens the quick fix modal for the current ticket and optionally primes
   * Contact User metadata when that action is included in the steps.
   */
  const openQuickFixModal = async () => {
    const currentTicket = ticket.value;
    const selectedActions = quickFixStepsMap[currentTicket.eventTrigger as TicketTrigger] || [];
    const hasContactUserAction = selectedActions.includes(TicketAction.CONTACT_USER);
    const item: QuickFixPayload<TTicketDetails> = {
      ticket: currentTicket,
      metadata: {
        selectedActions,
      },
    };

    if (hasContactUserAction) {
      const isDeviceEvent = !!currentTicket.lastLoggedInUsers?.length;
      const recipients = await adapter.getRecipients(
        currentTicket.eventId,
        isDeviceEvent,
        currentTicket.workspaceId
      );
      item.metadata[TicketAction.CONTACT_USER] = {
        includeEventDetails: true,
        contactMessage: "",
        recipients,
      };
    }

    trackGtmEvent(
      GtmAction.TICKETS,
      `${adapter.gtmContext} - ${GTMParams.QUICK_FIX} - ${startCase(currentTicket.eventTrigger)}`
    );

    dialogsStore.openDialog(
      componentDialogsConfigConstructor({
        action: TicketAction.APPLY_TICKET_QUICK_FIX,
        item,
        callback: adapter.applyQuickFixAction,
        component: ApplyQuickFixModal,
        disable: false,
        width: ModalWidth.LARGE,
        scrollable: true,
      })
    );
  };

  /**
   * Main action dispatcher for the header menu. Handles proxy configuration
   * guard, standard confirmation actions, and action-specific dialogs.
   */
  const onActionClick = async (
    { name: action, severity: actionSeverity }: TicketActionWithSeverity,
    item: TTicketDetails
  ) => {
    const severity = actionSeverity ?? TicketActionSeverity.MEDIUM;
    const disable = adapter.isActionRestricted(item, severity);
    const { showCheckbox, closeTicket, disclaimer, includeCloseTicket } = getCommonProps(
      item,
      adapter,
      disable
    );

    if (
      await handleProxyGuard({
        action,
        severity,
        item,
        adapter,
        dialogsStore,
      })
    ) {
      return;
    }

    if (standardConfirmationActions.includes(action)) {
      dialogsStore.openDialog(
        createTicketModalWithConfirmationConfig({
          action,
          item,
          disable,
          callback: adapter.applyAction,
          showCheckbox,
          closeTicket,
          disclaimer,
        })
      );
      return;
    }

    // Action-specific handlers; fallback is a generic confirmation modal.
    const handlers: Partial<Record<TicketAction, () => void | Promise<void>>> = {
      [TicketAction.MARK_AS_PROCESSED]: async () => {
        await adapter.applyAction({
          action,
          item: {
            ...item,
            ...(includeCloseTicket ? { closeTicket } : {}),
          },
        });
      },
      [TicketAction.MARK_AS_UNPROCESSED]: async () => {
        await adapter.applyAction({
          action,
          item,
        });
      },
      [TicketAction.DOWNLOAD_EML_FILE]: () => {
        dialogsStore.openDialog({
          ...createTicketModalWithConfirmationConfig({
            action,
            item,
            disable,
            callback: adapter.applyDownloadAction,
            showCheckbox,
            disclaimer,
          }),
          header: {
            title: i18n.global.t(`modals.${action}.title`),
            titleClass: "text-red-dark",
            titleIcon: "warning",
            close: true,
          },
        });
      },
      [TicketAction.EXPORT_MASS_DELETE_FILES]: () => {
        dialogsStore.openDialog(
          createTicketModalWithConfirmationConfig({
            action,
            item,
            disable,
            callback: adapter.applyDownloadAction,
            showCheckbox,
            disclaimer,
          })
        );
      },
      [TicketAction.EXPORT_MASS_DOWNLOAD_FILES]: () => {
        dialogsStore.openDialog(
          createTicketModalWithConfirmationConfig({
            action,
            item,
            disable,
            callback: adapter.applyDownloadAction,
            showCheckbox,
            disclaimer,
          })
        );
      },
      [TicketAction.TEST_MX_RECORD]: () => {
        if (adapter.onTestMxRecord) {
          adapter.onTestMxRecord(item, severity);
          return;
        }
        dialogsStore.openDialog(
          confirmationDialogsConfigConstructor({
            action,
            item,
            disable,
            callback: adapter.applyAction,
          })
        );
      },
      [TicketAction.APPROVE_EMAIL_WITH_OPTIONS]: () => {
        dialogsStore.openDialog(
          componentDialogsConfigConstructor({
            action,
            item: {
              ...item,
              allowAction: true,
              enabledActions: intersection(
                item.actions.map((value) => value.name),
                approveEmailActions
              ),
            },
            callback: adapter.applyAction,
            component: AllowBlockEmailTicketModal,
            disable,
            width: ModalWidth.MEDIUM,
            disclaimer,
          })
        );
      },
      [TicketAction.APPROVE_OUTBOUND_PROXY_EMAIL]: () => {
        dialogsStore.openDialog(
          componentDialogsConfigConstructor({
            action,
            item: {
              ...item,
            },
            callback: adapter.applyAction,
            component: AllowOutboundProxyEmailModal,
            disable,
            width: ModalWidth.MEDIUM,
            disclaimer,
          })
        );
      },
      [TicketAction.DISCARD_OUTBOUND_PROXY_EMAIL]: () => {
        dialogsStore.openDialog(
          createTicketModalWithConfirmationConfig({
            action,
            item,
            disable,
            callback: async (payload) => {
              await adapter.applyAction({
                ...payload,
                action: TicketAction.DISCARD_EMAIL,
              });
            },
            disclaimer,
          })
        );
      },
      [TicketAction.DISCARD_EMAIL_WITH_OPTIONS]: () => {
        dialogsStore.openDialog(
          componentDialogsConfigConstructor({
            action,
            item: {
              ...item,
              allowAction: false,
              enabledActions: intersection(
                item.actions.map((value) => value.name),
                discardEmailActions
              ),
            },
            callback: adapter.applyAction,
            component: AllowBlockEmailTicketModal,
            disable: false,
            width: ModalWidth.MEDIUM,
            disclaimer: "",
          })
        );
      },
      [TicketAction.CONTACT_USER]: () => handleContactUserAction(item, disable, disclaimer),
      [TicketAction.GENERAL_APPROVE]: () =>
        handleGeneralApproveAction(item, showCheckbox, disable, disclaimer),
      [TicketAction.ADD_TO_DATA_GOVERNANCE_PERMISSIONS]: () => {
        if (userDataGovernanceUnifiedEventTypes.includes(item.eventType)) {
          dialogsStore.openDialog({
            ...componentDialogsConfigConstructor({
              item,
              action,
              component: BulkAddDataGovernancePermissionsModal,
              width: ModalWidth.MEDIUM,
              callback: adapter.applyAction,
              disable,
            }),
          });
          return;
        }

        dialogsStore.openDialog(
          createTicketModalWithConfirmationConfig({
            action,
            item,
            disable,
            callback: async (payload) => {
              const requestPayload = {
                ...payload,
                metadata: {
                  selectedSubEventTriggers: [payload.item.eventTrigger],
                },
              };
              await adapter.applyAction(requestPayload);
            },
            showCheckbox,
            closeTicket,
            disclaimer,
          })
        );
      },
      [TicketAction.ALLOW_PROCESS]: () => {
        dialogsStore.openDialog(
          createTicketModalWithConfirmationConfig({
            action,
            item,
            disable,
            callback: adapter.applyAction,
            showCheckbox,
            closeTicket: true,
            disclaimer,
          })
        );
      },
      [TicketAction.PROCESS_PHISHING_EMAIL]: () => {
        const phishingDisable = isGlobalActionRestricted(
          GlobalRoleScopes.GLOBAL_SCOPE,
          GlobalRoleScopeSection.SPECIAL_PERMISSIONS,
          "reportPositiveEmail"
        );
        const phishingDisclaimer = getCommonProps(item, adapter, phishingDisable).disclaimer;

        dialogsStore.openDialog(
          createTicketModalWithConfirmationConfig({
            action,
            item,
            disable: phishingDisable,
            callback: adapter.applyAction,
            showCheckbox,
            disclaimer: phishingDisclaimer,
          })
        );
      },
      [TicketAction.ACCEPT_NEW_BASELINE]: () => {
        dialogsStore.openDialog(
          createTicketModalWithConfirmationConfig({
            action,
            item,
            disable,
            text: i18n.global.t(`modals.${action}.description`, {
              affectedDevicesCount: item.sections?.edrMetaData?.affectedDevicesCount,
            }),
            callback: adapter.applyAction,
            showCheckbox: true,
            closeTicket: true,
          })
        );
      },
      [TicketAction.IGNORE_FILE_INTEGRITY_VIOLATION]: () => {
        const affectedDevicesCount = item.sections?.edrMetaData?.affectedDevicesCount ?? 1;
        dialogsStore.openDialog(
          createTicketModalWithConfirmationConfig({
            action,
            item,
            disable,
            callback: adapter.applyAction,
            text: i18n.global.t(
              `modals.${action}.description`,
              {
                affectedDevicesCount,
                filePath: item.sections?.filesMetaData?.[0]?.filePath,
              },
              affectedDevicesCount
            ),
            showCheckbox,
            closeTicket: true,
          })
        );
      },
      [TicketAction.UNIGNORE_FILE_INTEGRITY_VIOLATION]: () => {
        const affectedDevicesCount = item.sections?.edrMetaData?.affectedDevicesCount ?? 1;

        dialogsStore.openDialog(
          createTicketModalWithConfirmationConfig({
            action,
            item,
            disable,
            callback: adapter.applyAction,
            text: i18n.global.t(
              `modals.${action}.description`,
              {
                affectedDevicesCount,
                filePath: item.sections?.filesMetaData?.[0]?.filePath,
              },
              affectedDevicesCount
            ),
          })
        );
      },
    };

    const handler = handlers[action];
    if (handler) {
      await handler();
      return;
    }

    // Default confirmation flow for unhandled actions.
    dialogsStore.openDialog(
      createTicketModalWithConfirmationConfig({
        action,
        item,
        disable,
        callback: adapter.applyAction,
        showCheckbox,
        disclaimer,
      })
    );
  };

  return {
    actionsList,
    onActionClick,
    openQuickFixModal,
  };
}
